/**
 * OphaalReminderCard — toont de ophaal-reminder-state op de order-detail page.
 *
 * Verschijnt wanneer:
 *  - WO actief in sequence (`reminderState.inSequence === true`), OF
 *  - WO geannuleerd om reden "Geen reactie op reminders" — dan toont card de
 *    heractivering-knop.
 *
 * Brein-klare service: knop roept POST /api/orders/reactivate-ophaal-reminder
 * aan, dat proxy is voor OphaalReminderReactivationService in Apex.
 */

import { useState } from 'react';
import clsx from 'clsx';
import type { OrderDetail } from '../../lib/orders';
import { reactivateOphaalReminder } from '../../lib/ophaal-reminder';

interface Props {
  order: OrderDetail;
  onReactivated: () => void;
}

const REMINDER_LABELS = ['S02A', 'S02B', 'S02C', 'S02D', 'S02E'] as const;
const SEQUENCE_LENGTH = REMINDER_LABELS.length;
const REMINDER_INTERVAL_DAYS = [2, 4, 6, 8, 10]; // dag-nummers vanaf startDate
const AUTO_CANCEL_DAYS = 14;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function daysFromNow(target: Date): number {
  const ms = target.getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function OphaalReminderCard({ order, onReactivated }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const r = order.reminderState;
  const isCancelledForReminders =
    order.status === 'Geannuleerd' && r?.redenAnnulering === 'Geen reactie op reminders';

  const visible = r?.inSequence || isCancelledForReminders;
  if (!visible || !r) return null;

  // Bereken next-mail en auto-cancel als sequence actief is.
  let nextMailLabel: string | null = null;
  let nextMailDate: string | null = null;
  let autoCancelDate: string | null = null;
  let autoCancelDaysLeft: number | null = null;
  if (r.inSequence && r.startDate) {
    const start = new Date(r.startDate);
    const nextIdx = Math.min(r.count, SEQUENCE_LENGTH - 1);
    nextMailLabel = REMINDER_LABELS[nextIdx] ?? null;
    const nextDay = REMINDER_INTERVAL_DAYS[nextIdx] ?? AUTO_CANCEL_DAYS;
    const nextDate = new Date(start);
    nextDate.setDate(nextDate.getDate() + nextDay);
    nextMailDate = formatDate(nextDate.toISOString());
    const cancel = new Date(start);
    cancel.setDate(cancel.getDate() + AUTO_CANCEL_DAYS);
    autoCancelDate = formatDate(cancel.toISOString());
    autoCancelDaysLeft = daysFromNow(cancel);
  }

  const handleReactivate = async () => {
    setBusy(true);
    setError(null);
    const res = await reactivateOphaalReminder(order.id);
    setBusy(false);
    setShowConfirm(false);
    if (res.ok) {
      onReactivated();
    } else {
      setError(res.error || res.message || 'Reactivering mislukt');
    }
  };

  // Status-pill kleur
  const statusPill = isCancelledForReminders
    ? { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', label: 'Geannuleerd' }
    : r.bounced
      ? { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Bounce' }
      : r.count >= 3
        ? { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', label: 'Actie vereist' }
        : { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', label: 'Actief' };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            Ophaal-reminder-sequence
          </h2>
          <span
            className={clsx(
              'text-[10px] px-1.5 py-0.5 rounded font-medium border',
              statusPill.bg,
              statusPill.text,
              statusPill.border
            )}
          >
            {statusPill.label}
          </span>
        </div>
      </div>

      {r.inSequence && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-slate-500">Verstuurd</div>
            <div className="font-medium text-slate-900">
              {r.count} van {SEQUENCE_LENGTH}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Sequence gestart</div>
            <div className="font-medium text-slate-900">{formatDate(r.startDate)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Volgende mail</div>
            <div className="font-medium text-slate-900">
              {nextMailLabel ? `${nextMailLabel} op ${nextMailDate}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Auto-cancel</div>
            <div
              className={clsx(
                'font-medium',
                autoCancelDaysLeft !== null && autoCancelDaysLeft <= 3
                  ? 'text-red-700'
                  : 'text-slate-900'
              )}
            >
              {autoCancelDate}
              {autoCancelDaysLeft !== null && autoCancelDaysLeft <= 3 && autoCancelDaysLeft >= 0 && (
                <span className="text-xs ml-1">({autoCancelDaysLeft}d)</span>
              )}
            </div>
          </div>
        </div>
      )}

      {r.inSequence && (r.bounced || r.telefoonTaskCreated) && (
        <div className="flex flex-col gap-1 text-xs">
          {r.bounced && (
            <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-red-50 border border-red-200 text-red-700">
              <span>⚠</span>
              <span>
                Email-bounce gedetecteerd — klant is niet via email te bereiken. Bel klant.
              </span>
            </div>
          )}
          {r.telefoonTaskCreated && (
            <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-700">
              <span>📞</span>
              <span>Telefoon-fallback-task is aangemaakt (3+ reminders verstuurd).</span>
            </div>
          )}
        </div>
      )}

      {isCancelledForReminders && (
        <div className="space-y-2">
          <p className="text-sm text-slate-600">
            Deze order is geannuleerd omdat de klant niet reageerde op de reminder-mails.
            Heractiveer om de WO terug in <code>Ophalen plannen</code> te zetten met een
            nieuwe sequence (count = 0).
          </p>
          {r.reactiveringBron && (
            <p className="text-xs text-slate-500">
              Eerder geheractiveerd via: <strong>{r.reactiveringBron}</strong>
            </p>
          )}
          {error && (
            <div className="text-xs px-2 py-1.5 rounded bg-red-50 border border-red-200 text-red-700">
              {error}
            </div>
          )}
          {!showConfirm ? (
            <button
              type="button"
              onClick={() => setShowConfirm(true)}
              disabled={busy}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Heractiveer reminder-sequence
            </button>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-200 bg-indigo-50">
              <span className="text-sm text-slate-700">
                Weet je het zeker? WO gaat terug naar <code>Ophalen plannen</code>, count = 0,
                eerste mail volgt over 2 dagen.
              </span>
              <button
                type="button"
                onClick={handleReactivate}
                disabled={busy}
                className="px-2 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {busy ? 'Bezig…' : 'Ja, heractiveer'}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={busy}
                className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
              >
                Annuleer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
